/**
 * useCustomerNotes — React Query hooks for the customer note stack (#872).
 *
 * Mirrors useSiteNotes: cache keys hang off `customerKeys.summary(id)` so
 * invalidating a customer's 360 cascades to its notes list, and the list result
 * is re-sorted client-side (`pinned DESC, position ASC, created_at ASC`) so an
 * optimistic pin toggle renders in the right place before the refetch lands.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerNotes } from '../api/methods';
import { customerKeys } from './useCustomerSummary';
export const customerNoteKeys = {
    list: (qbCustomerId, options = {}) => [
        ...customerKeys.summary(qbCustomerId),
        'notes',
        { includeArchived: Boolean(options.includeArchived) },
    ],
    versions: (qbCustomerId, noteId) => [...customerKeys.summary(qbCustomerId), 'notes', noteId, 'versions'],
};
/** Matches the server's ORDER BY so optimistic writes land in the right slot. */
function sortNotes(rows) {
    return [...rows].sort((a, b) => {
        if (a.pinned !== b.pinned)
            return a.pinned ? -1 : 1;
        if (a.position !== b.position)
            return a.position - b.position;
        const aTs = a.created_at ? Date.parse(a.created_at) : 0;
        const bTs = b.created_at ? Date.parse(b.created_at) : 0;
        return aTs - bTs;
    });
}
/** Every list-cache variant (archived toggle on or off) for one customer. */
function notesListKey(qbCustomerId) {
    return [...customerKeys.summary(qbCustomerId), 'notes'];
}
export function useCustomerNotes(qbCustomerId, options = {}) {
    return useQuery({
        queryKey: customerNoteKeys.list(qbCustomerId ?? '', options),
        queryFn: async () => sortNotes(await customerNotes.list(qbCustomerId, options)),
        enabled: !!qbCustomerId,
    });
}
export function useCreateCustomerNote(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => customerNotes.create(qbCustomerId, vars),
        onSuccess: (created) => {
            queryClient.setQueriesData({ queryKey: notesListKey(qbCustomerId) }, (old) => sortNotes([...(old || []), created]));
        },
    });
}
export function useUpdateCustomerNote(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ noteId, ...patch }) => customerNotes.update(qbCustomerId, noteId, patch),
        onSuccess: (updated) => {
            queryClient.setQueriesData({ queryKey: notesListKey(qbCustomerId) }, (old) => sortNotes((old || []).map((n) => (n.id === updated.id ? updated : n))));
            queryClient.invalidateQueries({
                queryKey: customerNoteKeys.versions(qbCustomerId, updated.id),
            });
        },
    });
}
export function useArchiveCustomerNote(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (noteId) => customerNotes.archive(qbCustomerId, noteId),
        onSuccess: (archived) => {
            // Drop from the active-only cache; includeArchived variants refetch.
            queryClient.setQueriesData({ queryKey: notesListKey(qbCustomerId) }, (old) => (old || []).filter((n) => n.id !== archived.id));
            queryClient.invalidateQueries({ queryKey: notesListKey(qbCustomerId) });
        },
    });
}
export function useDeleteCustomerNote(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (noteId) => customerNotes.remove(qbCustomerId, noteId),
        onSuccess: (_data, noteId) => {
            queryClient.setQueriesData({ queryKey: notesListKey(qbCustomerId) }, (old) => (old || []).filter((n) => n.id !== noteId));
        },
    });
}
export function useReorderCustomerNotes(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (items) => customerNotes.reorder(qbCustomerId, items),
        onSuccess: () => {
            // Reorder returns only the touched rows — refetch for the canonical order.
            queryClient.invalidateQueries({ queryKey: notesListKey(qbCustomerId) });
        },
    });
}
export function useCustomerNoteVersions(qbCustomerId, noteId, enabled = true) {
    return useQuery({
        queryKey: customerNoteKeys.versions(qbCustomerId, noteId),
        queryFn: () => customerNotes.listVersions(qbCustomerId, noteId),
        enabled: enabled && Boolean(qbCustomerId) && Boolean(noteId),
    });
}
//# sourceMappingURL=useCustomerNotes.js.map